import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { NotificationsNoneOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { Button, Callout, CardPreOferta, NextStep, PageHeader } from "@components/ui_v2";
import { formatCurrencyWhole, formatPercent } from "@helpers/funciones";
import { PATHS_EXPEDIENTE_V2 } from "@helpers/constantes";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetConditionsByIdQuery } from "@api/applications";
import { ApiError, LoanCondiciones } from "@interfaces/response";

const EMPRESAS_AUTORIZADAS: { nombre: string; monto: number }[] = [
  // { nombre: "Refacciones Vega SA", monto: 1150000 },
  // { nombre: "Chedraui", monto: 850000 },
  // { nombre: "Grupo Bimbo", monto: 550000 },
  // { nombre: "Cadena Comercial OXXO", monto: 550000 },
];
const VIGENCIA_DIAS = 15;

/** `.card-title` / `.card-sub` del diseño; en esta pantalla el subtítulo baja a 12.5px. */
const CardTitulo = ({ title, description, mb }: { title: string; description: string; mb: string }) => (
  <>
    <Typography sx={{ fontSize: 17, fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark", mb: "4px" }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 12.5, color: "tokens.textTertiary", lineHeight: 1.55, mb }}>{description}</Typography>
  </>
);

/**
 * Expediente autorizado para productos de factoraje (FLUX PM y PFAE): la línea autorizada, su reparto por
 * empresa y los pasos para activarla.
 */
export const ExpedienteAutorizadoFactoraje = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const application = useSelector((state: RootState) => state.register.application);
  const [getConditions] = useLazyGetConditionsByIdQuery();

  const [conditions, setConditions] = useState<LoanCondiciones | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadConditions = useCallback(async () => {
    if (!application?.id) {
      setIsLoading(false);
      return;
    }
    try {
      const data = await getConditions(application.id).unwrap();
      setConditions(data[0] ?? null);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpedienteAutorizadoFactoraje" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  }, [application?.id, getConditions, dispatch]);

  useEffect(() => {
    loadConditions();
  }, [loadConditions]);

  return (
    <Box>
      <PageHeader
        eyebrow="Formalización · Expediente autorizado"
        title="¡Tu expediente está autorizado!"
        subtitle={
          <>
            Revisamos toda tu información y documentos. La <strong>ratificación crediticia y legal</strong> está lista.
            El siguiente paso es firmar tu contrato de factoraje y activar la línea.
          </>
        }
      />

      {isLoading ? (
        <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
      ) : (
        <>
          <CardPreOferta
            eyebrow="Línea de factoraje autorizada"
            badge="Autorizada"
            amount={formatCurrencyWhole(conditions?.approved_amount ?? 0)}
            amountCaption="MXN revolventes"
            sub=""
            items={[
              { label: "Aforo", value: formatPercent(conditions?.extra_conditions?.factoring?.advance_rate ?? 0) },
              { label: "Tasa anual", value: formatPercent(conditions?.interest_rate ?? 0) },
              {
                label: "Comisión por operación",
                value: formatPercent(conditions?.extra_conditions?.factoring?.operation_fee ?? 0),
              },
              { label: "Plazo máximo de factura", value: `${conditions?.approved_max_term ?? 0} días` },
            ]}
          />

          {EMPRESAS_AUTORIZADAS.length > 0 && (
            <Paper variant="lendia_paper" sx={{ mt: "18px", p: "22px" }}>
              <CardTitulo
                title="Empresas autorizadas para factorar"
                description="Así quedó repartida tu línea. Puedes redistribuirla desde tu panel cuando la línea esté activa."
                mb="14px"
              />
              <Box
                sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "8px 20px" }}
              >
                {EMPRESAS_AUTORIZADAS.map((empresa) => (
                  <Box key={empresa.nombre} sx={{ display: "grid", gridTemplateColumns: "4px 1fr", gap: "10px" }}>
                    <Box sx={{ borderRadius: "3px", minHeight: 26, bgcolor: "primary.accent" }} />
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: "text.primary",
                          lineHeight: 1.3,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {empresa.nombre}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11.5,
                          color: "tokens.textTertiary",
                          fontVariantNumeric: "tabular-nums",
                          mt: "1px",
                        }}
                      >
                        {formatCurrencyWhole(empresa.monto)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          <Paper variant="lendia_paper" sx={{ mt: "18px", p: "22px" }}>
            <CardTitulo title="Lo que sigue" description="Tres pasos para activar tu línea." mb="18px" />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <NextStep
                n={1}
                title="Generación de contratos"
                description="Preparamos el contrato de factoraje y sus anexos con los datos validados de tu expediente."
              />
              <NextStep
                n={2}
                title="Revisión de condiciones"
                description="Confirmas monto de la línea, aforo, tasa y comisiones antes de firmar."
              />
              <NextStep
                n={3}
                title="Firma electrónica"
                description="Firmas los documentos con tu FIEL. Al firmar, tu línea queda activa para descontar facturas."
              />
            </Box>
            <Box sx={{ mt: "18px" }}>
              <Callout tone="info" icon={<NotificationsNoneOutlined sx={{ fontSize: 14 }} />}>
                Te enviamos una copia de este resumen por correo y WhatsApp. La autorización es vigente por{" "}
                <strong>{VIGENCIA_DIAS} días naturales</strong>.
              </Callout>
            </Box>
          </Paper>

          {/* `.actions.actions-end`; sin navegación todavía: solo maquetado. */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              mt: 4,
              pt: "20px",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button tone="outline">Descargar resumen</Button>
            <Button tone="primary" withArrow onClick={() => navigate(PATHS_EXPEDIENTE_V2.preparandoContratos.path)}>
              Continuar
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
